import { Divider, Stack, StackProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  EditorContentProps,
  getToolbarButtons,
  KEY_SHORTCUTS_MAC,
  KEY_SHORTCUTS_WINDOWS,
  ToolbarButtonsProps,
} from '@provision/rich-text-editor';
import React, { cloneElement, ReactElement } from 'react';
import { Tooltip } from 'src/components';
import { IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { toSxArray } from 'src/utils/sx';
import { Platform } from 'src/utils/system';
import { AddLinkPopover, useAddLinkPopover } from './AddLinkPopover';
import { EditorToolbarButton, EditorToolbarButtonSize } from './editor-toolbar-button';
import { EditorToolbarButtons } from './editor-toolbar-buttons';
import { StyledDynamicIcon } from './styled-dynamic-icon';

export const ToolbarIcon = ({
  data,
  size = 'medium',
  iconOverrides,
}: {
  data: {
    title: string | ReactElement;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isActive?: boolean;
    disabled?: boolean;
    icon: string;
    /**
     * Renders `icon` from this library directly, bypassing `iconOverrides` — for callers
     * outside the shared formatting-button map that already know which icon they want, so
     * the override map doesn't have to grow to reach components `EditorToolbar` never sees.
     */
    iconLibrary?: IconLibrary;
    testId?: string;
  };
  size?: EditorToolbarButtonSize;
  iconOverrides?: Record<string, string>;
}) => {
  const { title, onClick, isActive, disabled, icon, iconLibrary, testId } = data;
  const overrideIconName = iconOverrides?.[icon];
  const lucideIconName = iconLibrary === 'lucide' ? icon : overrideIconName;

  return (
    <Tooltip title={title} sx={{ whiteSpace: 'pre-wrap', display: 'inline-block' }}>
      <span>
        <EditorToolbarButton
          onClick={onClick}
          isActive={isActive}
          disabled={disabled}
          size={size}
          data-testid={testId}
        >
          {lucideIconName ? (
            <StyledDynamicIcon library="lucide" name={lucideIconName} size={16} />
          ) : (
            <StyledDynamicIcon filled name={icon} />
          )}
        </EditorToolbarButton>
      </span>
    </Tooltip>
  );
};

export type ToolbarOptionElement = ReactElement<{ editor: EditorContentProps['editor'] }>;

type EditorToolbarProps = EditorContentProps &
  StackProps & {
    toolbarOptions?: ToolbarOptionElement[];
    withToolBarOtherOptions?: boolean;
    toolbarButtonsProps?: Partial<ToolbarButtonsProps>;
    // `toolbarSize`, not `size`: this type intersects tiptap's `EditorContentProps`, whose
    // inherited numeric `size` attribute would collapse a string-literal prop to `never`.
    toolbarSize?: EditorToolbarButtonSize;
    /** Overrides specific formatting-button icons with a lucide glyph, keyed by their default icon name. */
    iconOverrides?: Record<string, string>;
    /** Formatting-button `method`s to render outside the collapsed formatting group. */
    pinnedFormattingMethods?: string[];
    renderFormatToggleIcon?: (isExpanded: boolean) => ReactElement;
  };

const DEFAULT_TOOLBAR_OPTIONS: ToolbarOptionElement[] = [];
const DEFAULT_PINNED_FORMATTING_METHODS: string[] = [];

const compactToolbarRowSx: SxProps<Theme> = { gap: 0.75 };
const defaultToolbarRowSx: SxProps<Theme> = (theme) => ({
  padding: theme.spacing(0.75, 0),
  gap: 0.25,
});

const compactDividerSx: SxProps<Theme> = (theme) => ({
  height: '1.125rem',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
});
const defaultDividerSx: SxProps<Theme> = { margin: '0 0.5rem' };

export const EditorToolbar = ({
  editor,
  toolbarOptions = DEFAULT_TOOLBAR_OPTIONS,
  withToolBarOtherOptions,
  toolbarButtonsProps,
  toolbarSize = 'medium',
  iconOverrides,
  pinnedFormattingMethods = DEFAULT_PINNED_FORMATTING_METHODS,
  renderFormatToggleIcon,
  sx,
  ...rest
}: EditorToolbarProps) => {
  const keyShortcuts = Platform.isMacOS ? KEY_SHORTCUTS_MAC : KEY_SHORTCUTS_WINDOWS;
  const isCompact = toolbarSize === 'small';

  const {
    anchorEl: linkPopoverAnchorEl,
    selectedText,
    openPopover: handleAddLink,
    closePopover: handleLinkPopoverClose,
    handleLinkSubmit,
  } = useAddLinkPopover({ editor });

  const { formatting: allFormattingButtons, other: otherButtons } = getToolbarButtons({
    editor,
    keyShortcuts,
    onLinkClick: handleAddLink,
    ...toolbarButtonsProps,
  });

  const pinnedButtons = pinnedFormattingMethods.length
    ? allFormattingButtons.filter((button) =>
        pinnedFormattingMethods.includes(button.method ?? ''),
      )
    : [];
  const formattingButtons = pinnedButtons.length
    ? allFormattingButtons.filter((button) => !pinnedButtons.includes(button))
    : allFormattingButtons;

  if (!editor) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={[
        // the mockup's border-top is RichTextEditor's own full-width Divider above this row
        isCompact ? compactToolbarRowSx : defaultToolbarRowSx,
        ...toSxArray(sx),
      ]}
      {...rest}
    >
      <AddLinkPopover
        open={!!linkPopoverAnchorEl}
        anchorEl={linkPopoverAnchorEl}
        onClose={handleLinkPopoverClose}
        onSubmit={handleLinkSubmit}
        initialTitle={selectedText}
      />
      <EditorToolbarButtons
        formattingButtons={formattingButtons}
        size={toolbarSize}
        iconOverrides={iconOverrides}
        renderToggleIcon={renderFormatToggleIcon}
      />
      {withToolBarOtherOptions ? (
        <>
          <Divider
            orientation="vertical"
            sx={isCompact ? compactDividerSx : defaultDividerSx}
          />
          {otherButtons?.map((button) => (
            <ToolbarIcon
              key={button.title}
              data={button}
              size={toolbarSize}
              iconOverrides={iconOverrides}
            />
          ))}
          {toolbarOptions.map((option) => cloneElement(option, { editor }))}
          {pinnedButtons.map((button) => (
            <ToolbarIcon
              key={button.title}
              data={button}
              size={toolbarSize}
              iconOverrides={iconOverrides}
            />
          ))}
        </>
      ) : null}
    </Stack>
  );
};
